import { axiosInstance } from "src/services/api";
import type {
  AcceptInviteResult,
  AddPrintedSetInput,
  CreatePartyInput,
  HostedParty,
  HostParty,
  HostPartyStats,
  MemberRole,
  PartyGuestRow,
  PartyInvite,
  PartyMember,
  PrintedSetOffer,
  ProContentType,
  ProSlot,
  PublishResult,
  UpdatePartyInput,
} from "./types";
import { normaliseHostedParty, normaliseHostStats } from "./host-normalise";
import type { QrStyle } from "./qr-style";

/**
 * Host endpoints use the logged-in user's auth (axiosInstance defaults) and never
 * the party guest token: no per-request headers here.
 */
const path = (id: string) => `/party-events/${encodeURIComponent(id)}`;

export const hostPartyApi = {
  async mine(): Promise<HostedParty[]> {
    const rows = (await axiosInstance.get<HostedParty[]>("/party-events/mine")).data;
    return Array.isArray(rows) ? rows.map(normaliseHostedParty) : rows;
  },
  async create(b: CreatePartyInput): Promise<HostParty> {
    return (await axiosInstance.post<HostParty>("/party-events", b)).data;
  },
  async stats(id: string): Promise<HostPartyStats> {
    return normaliseHostStats((await axiosInstance.get<HostPartyStats>(path(id))).data);
  },
  async guests(id: string): Promise<PartyGuestRow[]> {
    return (await axiosInstance.get<PartyGuestRow[]>(`${path(id)}/guests`)).data;
  },
  async update(id: string, b: UpdatePartyInput): Promise<HostParty> {
    return (await axiosInstance.patch<HostParty>(path(id), b)).data;
  },
  async reveal(id: string): Promise<HostParty> {
    return (await axiosInstance.post<HostParty>(`${path(id)}/reveal`)).data;
  },
  async goLive(id: string): Promise<HostParty> {
    return (await axiosInstance.post<HostParty>(`${path(id)}/go-live`)).data;
  },
  async remove(id: string): Promise<void> {
    await axiosInstance.delete(path(id));
  },
  async hidePhoto(id: string, photoId: string): Promise<void> {
    await axiosInstance.post(`${path(id)}/photos/${encodeURIComponent(photoId)}/hide`);
  },
  async unhidePhoto(id: string, photoId: string): Promise<void> {
    await axiosInstance.post(`${path(id)}/photos/${encodeURIComponent(photoId)}/unhide`);
  },

  /** A forwardable invite link for a role on this event. */
  async invite(id: string, role: MemberRole): Promise<PartyInvite> {
    return (await axiosInstance.post<PartyInvite>(`${path(id)}/invites`, { role })).data;
  },
  /** `businessName` (the studio) is stored only for photographer invites. */
  async acceptInvite(token: string, businessName?: string): Promise<AcceptInviteResult> {
    return (await axiosInstance.post<AcceptInviteResult>("/party-events/invites/accept", { token, ...(businessName ? { businessName } : {}) })).data;
  },
  async members(id: string): Promise<PartyMember[]> {
    return (await axiosInstance.get<PartyMember[]>(`${path(id)}/members`)).data;
  },
  /** The caller's own credit on their professional photos; null or blank clears it. */
  async setCredit(id: string, creditName: string | null): Promise<void> {
    await axiosInstance.patch(`${path(id)}/members/me`, { creditName });
  },
  /**
   * Organizer or lead. 409 `lead-cannot-be-removed` / `creator-cannot-be-removed` /
   * `cannot-remove-self`; their uploaded photos stay.
   */
  async removeMember(id: string, memberId: string): Promise<void> {
    await axiosInstance.delete(`${path(id)}/members/${encodeURIComponent(memberId)}`);
  },
  async setLead(id: string, memberId: string): Promise<void> {
    await axiosInstance.post(`${path(id)}/lead`, { memberId });
  },
  /** Lead only; 409 `nothing-to-publish` when nothing is pending. */
  async publish(id: string): Promise<PublishResult> {
    return (await axiosInstance.post<PublishResult>(`${path(id)}/publish`)).data;
  },
  /** Upload slot; PUT to `uploadUrl` with exactly the returned `contentType`. */
  async proSlot(id: string, body: { contentType: ProContentType; bytes: number }): Promise<ProSlot> {
    return (await axiosInstance.post<ProSlot>(`${path(id)}/pro-photos`, body)).data;
  },
  /** HTTP 200 even when rejected: check `status`. */
  async proComplete(id: string, photoId: string): Promise<{ status: "ready" | "rejected" }> {
    return (
      await axiosInstance.post<{ status: "ready" | "rejected" }>(
        `${path(id)}/pro-photos/${encodeURIComponent(photoId)}/complete`,
      )
    ).data;
  },
  async proDelete(id: string, photoId: string): Promise<void> {
    await axiosInstance.delete(`${path(id)}/pro-photos/${encodeURIComponent(photoId)}`);
  },

  /**
   * Creator only. The server renders the style and saves it only if it scans:
   * 422 `QR_UNREADABLE` otherwise (the old style stays); 400 `QR_COLOR`,
   * `QR_FRAME_TEXT` or `QR_IMAGE` for a malformed one.
   */
  async updateQrStyle(id: string, qrStyle: QrStyle): Promise<HostParty> {
    return (await axiosInstance.patch<HostParty>(path(id), { qrStyle })).data;
  },
  /**
   * Upload slot for a QR centre image (JPEG or PNG, at most 5 MB; 415 / 413 otherwise).
   * PUT the file to `uploadUrl` with exactly `contentType`, then save `key` as the style's centerImageKey.
   */
  async presignQrImage(id: string, body: { contentType: QrImageType; bytes: number }): Promise<QrImageSlot> {
    return (await axiosInstance.post<QrImageSlot>(`${path(id)}/qr-image`, body)).data;
  },
  /**
   * A short-lived link to the whole album as a ZIP (organizer, creator or photographer; 404 for anyone else).
   * Navigate the browser to `url` so it streams the file: never fetch it into memory.
   */
  async downloadAllLink(id: string): Promise<{ url: string; expiresAt: string }> {
    return (await axiosInstance.post<{ url: string; expiresAt: string }>(`${path(id)}/download-all/link`)).data;
  },
  /** The table cards and welcome sign on offer; null once the event day has passed (or for a caller who cannot edit). */
  async printedSet(id: string): Promise<PrintedSetOffer | null> {
    const data = (await axiosInstance.get<PrintedSetOffer | "" | null>(`${path(id)}/printed-set`)).data;
    return data || null;
  },
  /**
   * Adds the print files to the basket `orderId`. 400 `sign-unavailable` / `sign-size`, 403 `order-not-yours`,
   * 409 `PRINTED_SET_SIZES`, 422 `QR_UNREADABLE` (the saved QR style no longer scans).
   */
  async addPrintedSet(id: string, body: AddPrintedSetInput): Promise<{ added: number }> {
    return (await axiosInstance.post<{ added: number }>(`${path(id)}/printed-set`, body)).data;
  },
  /** The event QR as the server renders it in the saved style: a PNG `size` px wide (taller with a frame text). */
  /**
   * The event's name, start and a 30-minute signed URL of its 1200 px styled QR, for the
   * invitation editor opened from the app ("Uredite sami"): the `inviteToken` the app got
   * from POST :id/invitation-link is the auth, so this needs no login.
   */
  async invitationAssets(token: string): Promise<{ name: string; startsAt: string; qrPngUrl: string }> {
    return (await axiosInstance.get<{ name: string; startsAt: string; qrPngUrl: string }>("/party-events/invitation-assets", { params: { token } })).data;
  },
  async qrPng(id: string, size: number): Promise<Blob> {
    return (await axiosInstance.get<Blob>(`${path(id)}/qr.png`, { params: { size }, responseType: "blob" })).data;
  },
  /**
   * Upload slot for one "Pripravimo za vas" reference photo (JPEG, PNG or WebP, at most 15 MB; 415 / 413 otherwise).
   * PUT the file to `uploadUrl` with exactly `contentType`, then send `key` in the enquiry. Creator only.
   */
  async designEnquiryPhoto(id: string, body: { contentType: DesignEnquiryPhotoType; bytes: number }): Promise<DesignEnquiryPhotoSlot> {
    return (await axiosInstance.post<DesignEnquiryPhotoSlot>(`${path(id)}/design-enquiry/photo`, body)).data;
  },
  /**
   * Sends the enquiry to the shop (creator only; 10 a minute, else 429). 400 `photo-upload`: an uploaded photo
   * failed the server's check; `photo-key` / `too-many-photos` should not happen from this form.
   */
  async sendDesignEnquiry(id: string, body: DesignEnquiryInput): Promise<{ id: string; createdAt: string }> {
    return (await axiosInstance.post<{ id: string; createdAt: string }>(`${path(id)}/design-enquiry`, body)).data;
  },
};

export type DesignEnquiryPhotoType = "image/jpeg" | "image/png" | "image/webp";
export type DesignEnquiryItem = "invitation" | "menu" | "other";

/** POST /party-events/:id/design-enquiry/photo */
export interface DesignEnquiryPhotoSlot {
  key: string;
  uploadUrl: string;
  contentType: DesignEnquiryPhotoType;
}

/** POST /party-events/:id/design-enquiry */
export interface DesignEnquiryInput {
  item: DesignEnquiryItem;
  text: string;
  photoKeys: string[];
  contactEmail: string;
  contactPhone?: string;
}

export type QrImageType = "image/jpeg" | "image/png";

/** POST /party-events/:id/qr-image */
export interface QrImageSlot {
  key: string;
  uploadUrl: string;
  contentType: QrImageType;
}
